import { useEffect, useState, useCallback } from "react";
import { getAllPlans, updatePlan } from "../../../shared/services/adminApi";
import { EditIcon } from "../../../shared/icons/Icons";

const AdminPlanList = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPlan, setEditingPlan] = useState(null);
    const [editForm, setEditForm] = useState({
        name: "",
        code: "",
        summary: "",
        description: "",
        is_active: true,
        display_order: 0,
    });
    const [saving, setSaving] = useState(false);

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getAllPlans(1, 100);
            const data = response?.data?.data || response?.data || [];
            setPlans(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching plans:", error);
            setPlans([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const handleEditClick = (plan) => {
        setEditingPlan(plan);
        setEditForm({
            name: plan.name || "",
            code: plan.code || "",
            summary: plan.summary || "",
            description: plan.description || "",
            is_active: plan.is_active ?? true,
            display_order: plan.display_order || 0,
        });
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await updatePlan(editingPlan._id || editingPlan.id, editForm);
            setEditingPlan(null);
            fetchPlans();
        } catch (error) {
            console.error("Error updating plan:", error);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingPlan(null);
    };

    // Inline edit form
    if (editingPlan) {
        return (
            <div className="admin-table-container">
                <div className="admin-table-header">
                    <h4>Edit Plan: {editingPlan.name}</h4>
                </div>
                <form onSubmit={handleEditSubmit}>
                    <div style={{ maxWidth: "600px" }}>
                        <div className="form-group" style={{ marginBottom: "1rem" }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: "0.3rem", fontSize: "0.85rem" }}>Name</label>
                            <input
                                type="text"
                                name="name"
                                value={editForm.name}
                                onChange={handleEditChange}
                                style={{ width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #e5e7eb", borderRadius: "0.5rem" }}
                                required
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: "1rem" }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: "0.3rem", fontSize: "0.85rem" }}>Code</label>
                            <input
                                type="text"
                                name="code"
                                value={editForm.code}
                                onChange={handleEditChange}
                                style={{ width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #e5e7eb", borderRadius: "0.5rem" }}
                                required
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: "1rem" }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: "0.3rem", fontSize: "0.85rem" }}>Summary</label>
                            <textarea
                                name="summary"
                                value={editForm.summary}
                                onChange={handleEditChange}
                                rows={3}
                                style={{ width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #e5e7eb", borderRadius: "0.5rem", resize: "vertical" }}
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: "1rem" }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: "0.3rem", fontSize: "0.85rem" }}>Description</label>
                            <textarea
                                name="description"
                                value={editForm.description}
                                onChange={handleEditChange}
                                rows={3}
                                style={{ width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #e5e7eb", borderRadius: "0.5rem", resize: "vertical" }}
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: "1rem" }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: "0.3rem", fontSize: "0.85rem" }}>Display Order</label>
                            <input
                                type="number"
                                name="display_order"
                                value={editForm.display_order}
                                onChange={handleEditChange}
                                style={{ width: "100px", padding: "0.5rem 0.75rem", border: "1px solid #e5e7eb", borderRadius: "0.5rem" }}
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: "1rem" }}>
                            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 600, fontSize: "0.85rem" }}>
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={editForm.is_active}
                                    onChange={handleEditChange}
                                />
                                Active
                            </label>
                        </div>
                        <div style={{ display: "flex", gap: "0.75rem" }}>
                            <button type="submit" className="admin-action-btn btn-primary-action" disabled={saving}>
                                {saving ? "Saving..." : "Save Plan"}
                            </button>
                            <button type="button" className="admin-action-btn" onClick={handleCancelEdit}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div>
            <div className="admin-table-container">
                <div className="admin-table-header">
                    <h4>All Plans ({plans.length})</h4>
                </div>

                {loading ? (
                    <div className="admin-loading">
                        <div className="spinner"></div>
                    </div>
                ) : plans.length === 0 ? (
                    <div className="admin-empty-state">
                        <div className="empty-icon">📋</div>
                        <p>No plans found</p>
                    </div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Code</th>
                                <th>Summary</th>
                                <th>Active</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plans.map((plan, index) => (
                                <tr key={plan._id || plan.id || index}>
                                    <td>{index + 1}</td>
                                    <td style={{ fontWeight: 600 }}>{plan.name}</td>
                                    <td>{plan.code}</td>
                                    <td style={{ maxWidth: "300px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {plan.summary || "- - -"}
                                    </td>
                                    <td>
                                        <span className={`badge ${plan.is_active ? "bg-success" : "bg-danger"}`}>
                                            {plan.is_active ? "Active" : "Inactive"}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className="admin-action-btn btn-edit"
                                            onClick={() => handleEditClick(plan)}
                                            title="Edit Plan"
                                        >
                                            <EditIcon size={14} /> Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AdminPlanList;